import { SelectionTool, Tool, Web3DViewer } from '@technology/web3d';
import { FreelineMarkupTool } from '@technology/web3d-plugin-markup';
import {
  isDrawMarkupCreationAllowedForWrapper,
  Web3DViewerWrapper,
} from '@trimble/web3d-utils';
import { createEffect, createRoot } from 'solid-js';

export interface MarkupPerViewerState {
  readonly selectionTool: SelectionTool;
  readonly markupTools: ReadonlyMap<string, Tool>;
}

export type GetViewerWrappers = () => Web3DViewerWrapper[];

export interface DrawCreationGateEffectOptions {
  getViewerWrappers: GetViewerWrappers;
  getPerViewerState(): ReadonlyMap<Web3DViewer, MarkupPerViewerState>;
  getActiveToolName(): string | undefined;
  enableToolOnViewer(viewer: Web3DViewer, toolName: string): void;
}

export function isDrawCreationAllowedForViewer(
  viewer: Web3DViewer,
  getViewerWrappers: GetViewerWrappers
): boolean {
  const wrapper = getViewerWrappers().find((w) => w.viewer === viewer);
  if (!wrapper) {
    return true;
  }
  return isDrawMarkupCreationAllowedForWrapper(wrapper);
}

/** Gated panes (e.g. panorama) must keep their navigation tool; do not force SelectionTool. */
export function shouldRestoreSelectionOnViewer(
  viewer: Web3DViewer,
  toolName: string,
  getViewerWrappers: GetViewerWrappers
): boolean {
  return !(
    toolName === FreelineMarkupTool.Name &&
    !isDrawCreationAllowedForViewer(viewer, getViewerWrappers)
  );
}

export function enableMarkupToolOnViewer(
  viewer: Web3DViewer,
  toolName: string,
  state: MarkupPerViewerState,
  getViewerWrappers: GetViewerWrappers
): void {
  const tool = state.markupTools.get(toolName);
  if (!tool) {
    return;
  }

  if (
    toolName === FreelineMarkupTool.Name &&
    !isDrawCreationAllowedForViewer(viewer, getViewerWrappers)
  ) {
    // Only restore SelectionTool if freeline was actually active on this
    // viewer. Forcing selection on gated panes (e.g. panorama) can override
    // navigation controls like PanoramaControls.
    const wasFreelineEnabled = tool.enabled;
    tool.enabled = false;
    if (wasFreelineEnabled) {
      state.selectionTool.enabled = true;
    }
    return;
  }

  state.selectionTool.enabled = false;
  tool.enabled = true;
}

export function restoreSelectionOnViewerIfAllowed(
  viewer: Web3DViewer,
  toolName: string,
  state: MarkupPerViewerState,
  getViewerWrappers: GetViewerWrappers
): void {
  if (shouldRestoreSelectionOnViewer(viewer, toolName, getViewerWrappers)) {
    state.selectionTool.enabled = true;
  }
}

/**
 * Re-applies freeline enablement when pane mode / point-cloud presence changes
 * while draw markup is active.
 */
export function createDrawCreationGateEffect(
  options: DrawCreationGateEffectOptions
): () => void {
  return createRoot((dispose) => {
    createEffect(() => {
      const wrappers = options.getViewerWrappers();
      for (const wrapper of wrappers) {
        void wrapper.isDrawMarkupCreationAllowed?.();
      }

      if (options.getActiveToolName() !== FreelineMarkupTool.Name) {
        return;
      }

      for (const viewer of options.getPerViewerState().keys()) {
        options.enableToolOnViewer(viewer, FreelineMarkupTool.Name);
      }
    });
    return dispose;
  });
}
